import { useState } from 'react';
import { LoaderIcon } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import SingleOrder from './SingleOrder';
import { TFunction } from 'i18next';
import {
  StyledPagination,
  StyledTable,
} from '@/commons/ui/theme/materialStyles';
import useUserLineItemsPagination, {
  PLineItem,
} from '@/hooks/useUserLineItemsPagination';
import { OrderStatus } from '@/types';
import { TableCell } from '@mui/material';

interface UserOrdersTableProps {
  handleOrderAgain: (lineItem: PLineItem) => void;
}
type TableKey = keyof PLineItem | keyof PLineItem['order'];

const TABLE_HEAD = (
  t: TFunction,
): {
  id: TableKey;
  label: string;
  alignRight: boolean;
}[] => [
  {
    id: 'id',
    label: t('orders.table_columns.order_id'),
    alignRight: false,
  },
  {
    id: 'name',
    label: t('orders.table_columns.article_name'),
    alignRight: false,
  },
  {
    id: 'lineTotalNet',
    label: t('orders.table_columns.order_total_net'),
    alignRight: false,
  },
  {
    id: 'confirmedAt',
    label: t('orders.table_columns.created_at'),
    alignRight: false,
  },
];

export const getStylesByColor = (status: OrderStatus) => {
  switch (status) {
    case OrderStatus.Cancelled:
      return 'bg-red-100 text-[#B54708]';
    case OrderStatus.Pending:
      return 'bg-secondary-yellow text-[#B54708]';
    case OrderStatus.Complete:
      return 'bg-secondary-green text-primary-green';
    case OrderStatus.Confirmed:
      return 'bg-secondary-blue text-primary-blue';
  }
};

export default function UserOrdersTable({
  handleOrderAgain,
}: UserOrdersTableProps) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const navigate = useNavigate();
  const { retailerId } = useParams();

  const { result, fetching, refetch, loadMore, loadPrevious, total } =
    useUserLineItemsPagination(20);

  const handleRowClick = (lineItem: PLineItem) => {
    navigate(`/documents/${retailerId}/orders/${lineItem.order.id}`);
  };

  const handlePageChange = (e, newPage) => {
    if (newPage > page) {
      setPage((val) => val + 1);
      loadMore();
    } else {
      setPage((val) => val - 1);
      loadPrevious();
    }
  };

  const handleRowsPerPageChange = (e) => {
    setPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <div className="py-2-0">
      <StyledTable stickyHeader>
        <thead>
          <tr>
            {TABLE_HEAD(t).map((headCell) => (
              <TableCell
                key={headCell.id}
                align={headCell.alignRight ? 'right' : 'left'}
              >
                <span className="text-body1 font-rg opacity-80 capitalize">
                  {headCell.label}
                </span>
              </TableCell>
            ))}
          </tr>
        </thead>
        <tbody>
          {result.map((lineItem) => (
            <SingleOrder
              key={lineItem.id}
              lineItem={lineItem}
              onOrderAgainPress={handleOrderAgain}
              onClick={() => handleRowClick(lineItem)}
            />
          ))}
          <tr style={{ width: '100%' }}>
            <StyledPagination
              count={total}
              page={page}
              rowsPerPage={perPage}
              rowsPerPageOptions={[20, 50, 100]}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              labelRowsPerPage={t('global.rows_per_age')}
            />
          </tr>
        </tbody>
      </StyledTable>
      {fetching && <LoaderIcon className="mx-auto mt-10-0" />}
      {!fetching && result.length === 0 && (
        <p className="text-body1 py-4-0 font-bd text-center">
          {t('order.order_not_found')}
        </p>
      )}
    </div>
  );
}
